import SearchBarComponent from "components/customs/searchbarV2";
import CustomDataTable from "components/customs/table";
import React, { memo, useCallback, useMemo, useState } from "react";
import { defaultTableOption } from "constants/table";
import { getFormattedNumber } from "utility/helpers";
import styled from "styled-components";
import AddProjectCourseColumns from "./columns";

const CourseTableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  margin-top: 1rem;
`;

const AddCourseTbl = styled.div`
  padding-left: 0.75rem;
  padding-right: 0.75rem;
  margin-top: 2rem;
  margin-bottom: 2rem;
  border-top: solid #cecece 1px;
  border-bottom: solid #cecece 1px;
  width: 100%;
`;

const AddCourseTableContainer = styled.div``;

const Container = styled.div`
  display: flex;
  width: 100%;
`;

const TableHeaderText = styled.h5`
  margin: 0;
`;

const SearchBarWrapper = styled.div`
  width: 20%;
  position: relative;

  input {
    border: 2px solid ${({ theme }) => theme.colors.black60};
    background: white;
    box-shadow: none;
    border-radius: 9px;
    height: clamp(35px, 5vw, 50px);
  }
`;

const TotalSlotsText = styled.span`
  letter-spacing: 0.1rem;
  font-weight: 600;
`;

const TotalEstimatedCostText = styled.span`
  float: inline-end;
  letter-spacing: 0.1rem;
  font-weight: 600;
`;

const CoursesTable = ({
  data,
  selectedCourses,
  setSelectedCourses,
  isDisabled = false,
  form,
  setForm,
  idx,
  action,
}) => {
  const [tableOption, setTableOption] = useState(defaultTableOption);

  let changeSearchTimeoutID;

  const onChangeSearch = (event) => {
    event.persist();

    if (changeSearchTimeoutID) {
      clearTimeout(changeSearchTimeoutID);
    }

    changeSearchTimeoutID = setTimeout(() => {
      const eventValue = event.target.value.trim();

      if (eventValue.length !== 0) {
        const dataTable = tableOption.data.filter((item) =>
          item?.course?.toLowerCase()?.includes(eventValue)
        );

        setTableOption({
          ...tableOption,
          data: dataTable,
        });
      } else {
        setTableOption({
          ...tableOption,
          data,
        });
      }
    }, 1000);
  };

  useMemo(() => {
    setTableOption({
      ...tableOption,
      data,
    });
  }, [data]);

  const totalSlots = useCallback(() => {
    return (
      selectedCourses?.reduce((sum, course) => {
        const slots = course.slots || 0;

        return sum + slots;
      }, 0) ?? 0
    );
  }, [selectedCourses]);

  const totalEstimatedCost = selectedCourses.reduce((sum, course) => {
    const courseData = data.find((item) => item.id === course.id);
    const cost = courseData ? Number(courseData.totalCost) || 0 : 0;
    const slots = Number(course.slots) || 0;

    return sum + cost * slots;
  }, 0);

  useMemo(() => {
    const val = totalEstimatedCost;

    if (idx !== undefined) {
      const newForm = [...form];

      newForm[idx] = {
        ...newForm[idx],
        budgetAllocation: val,
      };
      setForm(newForm);
    } else if (selectedCourses.length !== 0) {
      setForm({
        ...form,
        // eslint-disable-next-line camelcase
        budget_allocation: val,
      });
    }
  }, [totalEstimatedCost, setForm]);

  const handleCheckboxOnChange = (row, evt) => {
    const { checked } = evt.target;

    setSelectedCourses((prevCourses) => {
      let updatedCourses = prevCourses.filter((course) => course.id !== row.id);

      if (checked) {
        updatedCourses = [
          ...updatedCourses,
          {
            tviCourseId: row.tviCourseId,
            slots: 0,
            ...(row.id != null ? { id: row.id } : {}),
          },
        ];
      }

      return updatedCourses;
    });
  };

  const handleBulkCheckboxOnChange = (evt) => {
    const { checked } = evt.target;
    const newData = data.filter((course) => course.availableSlots > 0);
    const newCourses = checked ? newData : [];

    setSelectedCourses(newCourses);
  };

  const handleSlotsOnChange = (tviCourseId, value) => {
    const id = selectedCourses.findIndex((course) => course.id === tviCourseId);

    const newCourses = [...selectedCourses];

    newCourses[id] = {
      ...newCourses[id],
      slots: Number(value),
    };

    setSelectedCourses(newCourses);
    const newData = [...tableOption.data];
    const index = newData.findIndex((course) => course.id === tviCourseId);

    newData[index] = {
      ...newData[index],
      slots: Number(value),
    };

    setTableOption({
      ...tableOption,
      data: newData,
    });
  };

  const columns = AddProjectCourseColumns({
    handleBulkCheckboxOnChange,
    isDisabled,
    selectedCourses,
    data: tableOption.data,
    handleCheckboxOnChange,
    action,
    handleSlotsOnChange,
  });

  return (
    <Container>
      <AddCourseTbl>
        <CourseTableHeader>
          <TableHeaderText>{"Add Courses to this TVI"}</TableHeaderText>

          <SearchBarWrapper>
            <SearchBarComponent
              onChangeSearch={onChangeSearch}
              isDisabled={tableOption?.isLoading}
            />
          </SearchBarWrapper>
        </CourseTableHeader>

        <AddCourseTableContainer>
          <CustomDataTable
            columns={columns}
            data={tableOption.data}
            noHeader
            progressPending={tableOption.isLoading}
            height="calc(100vh - 200px)"
            paginationPerPage={50}
            paginationRowsPerPageOptions={[5, 10, 25, 50, 100]}
          />

          <div>
            <TotalSlotsText>{`Total Slots: ${totalSlots()}`}</TotalSlotsText>
            {/* eslint-disable-next-line max-len */}
            <TotalEstimatedCostText>{`Total Estimated Cost: ${getFormattedNumber(
              totalEstimatedCost
            )}`}</TotalEstimatedCostText>
          </div>
        </AddCourseTableContainer>
      </AddCourseTbl>
    </Container>
  );
};

export default memo(CoursesTable);
